import React, { useState, useEffect } from "react";
import axios from "axios";
import { GET_MEASURES_URL, MEASURES_UPDATE_URL } from "../../api/urls";
import { TitleText, LabelText } from "../../ui/Text";
import styled, { keyframes } from "styled-components";
import { Button } from "../../ui/Button";
import { toast } from "react-toastify";

interface Measures {
  id: string;
  name: string;
  kpi_id: string;
  scorecard_type: string;
  status: string;
  owner_name: string;
  actual_value: number;
  target: number;
  comments: string;
}

interface MeasuresHeaderProps {
  hasPendingStatus: boolean;
}

interface MeasuresContentProps {
  hasPendingStatus: boolean;
}

const MeasuresTab = ({ user_id, status, scorecard_id }) => {
  const [measures, setMeasures] = useState<Measures[]>([]);

  const accessToken = localStorage.getItem("access_token");

  useEffect(() => {
    const fetchMeasures = async () => {
      try {
        const response = await axios.get(
          `${GET_MEASURES_URL}?user_id=${user_id}&status=${status}&scorecard_id=${scorecard_id}`,
        );
        setMeasures(response.data);
      } catch (error) {
        console.error("Error fetching measures:", error);
        setMeasures([]);
      }
    };

    fetchMeasures();
  }, [user_id, status]);

  const handleCancelApproval = async (measureId: string) => {
    try {
      await axios.post(
        `${MEASURES_UPDATE_URL}`,
        {
          status: "cancelled",
          id: measureId,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          }
        }
      );
      setMeasures(measures.filter((measure) => measure.id !== measureId));
      toast.success("You have successfully cancelled the request for approval");
    } catch (error) {
      toast.error("Error cancelling approval");
    }
  };

  return (
    <MeasuresContainer>
      <MeasuresHeader hasPendingStatus={status === "pending-approval"}>
        <LabelText>Measure Name</LabelText>
        <LabelText>Target</LabelText>
        <LabelText>Actual</LabelText>
        {status !== "pending-approval" && <LabelText>Comments</LabelText>}
      </MeasuresHeader>
      {measures.map((measure) => (
        <MeasuresContent
          key={measure.id}
          hasPendingStatus={measure.status === "pending-approval"}
        >
          <ContentContainer>
            <TitleText>{measure.name}</TitleText>
          </ContentContainer>
          <ContentContainer>
            <TitleText>{measure.target}</TitleText>
          </ContentContainer>
          <ContentContainer>
            <TitleText>{measure.actual_value}</TitleText>
          </ContentContainer>
          {status !== "pending-approval" && (
            <ContentContainer>
              <TitleText>{measure.comments}</TitleText>
            </ContentContainer>
          )}
          {measure.status === "pending-approval" && (
            <ButtonContainer>
              <CancelButton
                text="Cancel Approval"
                onClick={() => handleCancelApproval(measure.id)}
              />
            </ButtonContainer>
          )}
        </MeasuresContent>
      ))}
    </MeasuresContainer>
  );
};

export default MeasuresTab;

// Keyframe animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const hoverEffect = keyframes`
  from {
    background-color: #f5f6fb;
  }
  to {
    background-color: #e2e4f2;
  }
`;

// Styled Components
const MeasuresContainer = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  padding-bottom: 10em;
  animation: ${fadeIn} 0.5s ease-in-out;
`;

const MeasuresHeader = styled.div<MeasuresHeaderProps>`
  display: grid;
  grid-template-columns: repeat(3, 1fr) ${(props) =>
    props.hasPendingStatus ? "1fr" : "2fr"};
  grid-gap: 1em;
  margin: 1em 0;
`;

const MeasuresContent = styled.div<MeasuresContentProps>`
  display: grid;
  grid-template-columns: repeat(3, 1fr) ${(props) =>
    props.hasPendingStatus ? "1fr" : "2fr"};
  grid-gap: 1em;
  margin-top: 1em;
  padding: 0.75em;
  border-radius: 8px;
  background-color: #f5f6fb;
  transition: background-color 0.3s ease;
  &:hover {
    animation: ${hoverEffect} 0.3s forwards;
  }
  ${TitleText} {
    font-size: 14px;
  }
`;

const ContentContainer = styled.div`
  background-color: #f5f6fb;
  padding: 0.5em;
  border-radius: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CancelButton = styled(Button)`
  background-color: red;
  color: white;
  padding: 0.5em 1em;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background-color: darkred;
  }
`;
